import { useCallback, useContext, useState } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClientContext,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getUserPreferences,
  updateUserPreference,
} from "@/services/userPreferencesApi";
import { getAuthToken } from "@/services/apiClient";

/**
 * Shared React Query key for the full preferences blob. All
 * `useUserPreference` instances read from the same cache entry so a
 * single network round-trip hydrates every preference on the page.
 */
const PREFERENCES_QUERY_KEY = ["user-preferences"] as const;

export interface UseUserPreferenceOptions<T> {
  /** Dot-namespaced key sent to the backend, e.g. `device.staleThresholdDays`. */
  key: string;
  /** Value used before the server responds and when the server has no value. */
  defaultValue: T;
  /**
   * Optional validator. Return `true` when the raw value is acceptable.
   * Invalid remote values are ignored and `defaultValue` is used instead.
   */
  validate?: (value: unknown) => value is T;
  /**
   * Optional localStorage key to mirror the value to. Provides a
   * synchronous starting value on first paint and a fallback when the
   * preferences endpoint is unavailable. Omit to disable mirroring.
   */
  localStorageKey?: string;
  /**
   * Toast shown when the server rejects an optimistic update. Defaults
   * to a generic copy.
   */
  errorMessage?: string;
}

function readLocal<T>(
  storageKey: string | undefined,
  validate: ((v: unknown) => v is T) | undefined,
  fallback: T
): T {
  if (!storageKey || typeof window === "undefined") return fallback;
  const raw = window.localStorage.getItem(storageKey);
  if (raw === null) return fallback;
  let parsed: unknown = raw;
  try {
    parsed = JSON.parse(raw);
  } catch {
    const asNumber = Number(raw);
    parsed = Number.isNaN(asNumber) ? raw : asNumber;
  }
  if (!validate || validate(parsed)) return parsed as T;
  return fallback;
}

function writeLocal<T>(storageKey: string | undefined, value: T) {
  if (!storageKey || typeof window === "undefined") return;
  window.localStorage.setItem(storageKey, JSON.stringify(value));
}

/**
 * React Query–backed implementation. Reads the full preferences blob
 * once and shares it across all instances; persists optimistically and
 * reverts with a toast on failure.
 */
function useUserPreferenceWithQuery<T>(
  options: UseUserPreferenceOptions<T>
): { value: T; setValue: (next: T) => void; isSaving: boolean } {
  const { key, defaultValue, validate, localStorageKey, errorMessage } = options;
  const queryClient = useQueryClient();
  const initial = readLocal(localStorageKey, validate, defaultValue);

  const { data: prefs } = useQuery({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: async () => {
      try {
        return await getUserPreferences();
      } catch {
        return {} as Record<string, unknown>;
      }
    },
    // Don't fire until a JWT is present — otherwise the call goes out as
    // anonymous and the backend returns 401, then re-fires under load and
    // returns 500. Gating eliminates the visible error noise.
    enabled: typeof window !== "undefined" && !!getAuthToken(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  let value: T = initial;
  if (prefs && key in prefs) {
    const remote = prefs[key];
    if (!validate || validate(remote)) {
      value = remote as T;
      writeLocal(localStorageKey, value);
    }
  }

  const mutation = useMutation({
    mutationFn: (next: T) => updateUserPreference(key, next),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: PREFERENCES_QUERY_KEY });
      const previous =
        queryClient.getQueryData<Record<string, unknown>>(PREFERENCES_QUERY_KEY);
      queryClient.setQueryData<Record<string, unknown>>(
        PREFERENCES_QUERY_KEY,
        { ...(previous ?? {}), [key]: next }
      );
      writeLocal(localStorageKey, next);
      return { previous };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(PREFERENCES_QUERY_KEY, ctx.previous);
        const reverted = ctx.previous?.[key];
        if (reverted !== undefined && (!validate || validate(reverted))) {
          writeLocal(localStorageKey, reverted as T);
        }
      }
      toast.error(
        errorMessage ?? "Couldn't save your preference. Reverted to the previous value."
      );
    },
    onSuccess: (serverPrefs) => {
      if (serverPrefs && typeof serverPrefs === "object") {
        queryClient.setQueryData(PREFERENCES_QUERY_KEY, serverPrefs);
      }
    },
  });

  const setValue = useCallback(
    (next: T) => mutation.mutate(next),
    [mutation]
  );

  return { value, setValue, isSaving: mutation.isPending };
}

/**
 * Fallback implementation for when no `QueryClientProvider` is present
 * (isolated tests, Storybook). Persists to localStorage only.
 */
function useUserPreferenceLocalOnly<T>(
  options: UseUserPreferenceOptions<T>
): { value: T; setValue: (next: T) => void; isSaving: boolean } {
  const { defaultValue, validate, localStorageKey } = options;
  const [value, setLocalValue] = useState<T>(() =>
    readLocal(localStorageKey, validate, defaultValue)
  );

  const setValue = useCallback(
    (next: T) => {
      writeLocal(localStorageKey, next);
      setLocalValue(next);
    },
    [localStorageKey]
  );

  return { value, setValue, isSaving: false };
}

/**
 * Generic per-user preference hook backed by `/api/v1/admin/preferences`.
 *
 * - Reads the full preferences blob once via React Query and picks a
 *   single key out of it, so multiple preferences on the same page share
 *   one request.
 * - Optimistically updates the cache (and an optional localStorage
 *   mirror) on save, reverting with a toast if the server rejects.
 * - Falls back gracefully to `defaultValue` (or the local mirror) when
 *   the endpoint is unavailable.
 * - Works without a `QueryClientProvider` (degraded localStorage-only
 *   mode) so shared UI primitives like `<DataTable>` and `<GlassCard>`
 *   that use this hook stay usable in isolated tests and stories.
 */
export function useUserPreference<T>(
  options: UseUserPreferenceOptions<T>
): { value: T; setValue: (next: T) => void; isSaving: boolean } {
  // Selecting between two implementations is safe under rules-of-hooks
  // because the QueryClient context identity is stable for the lifetime
  // of a component — a tree never gains/loses a QueryClientProvider
  // mid-mount.
  const hasQueryClient = useContext(QueryClientContext) !== undefined;
  return hasQueryClient
    ? useUserPreferenceWithQuery(options)
    : useUserPreferenceLocalOnly(options);
}
